import React, { useState, useEffect } from "react";
import { X, Loader2, ShoppingCart, ArrowLeft } from "lucide-react";
import { brand } from "../mock/content";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inp = "w-full bg-transparent border border-white/25 px-3 py-2.5 text-sm text-white focus:border-[#F9E212] outline-none placeholder:text-white/30";
const lbl = "block text-white/50 text-[11px] uppercase tracking-[0.1em] mb-1";

export const CheckoutFunnel = ({ open, onClose, slug, qty, zone, pickup = false, quote, productName, cartItems, onSuccessClearCart }) => {
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", cap: "", city: "", province: "",
  });
  const [shipSame, setShipSame] = useState(true);
  const [ship, setShip] = useState({ address: "", cap: "", city: "", province: zone || "", notes: "" });
  const [tipo, setTipo] = useState("privato");
  const [inv, setInv] = useState({ ragione_sociale: "", piva: "", codice_fiscale: "", codice_destinatario: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && zone) setShip((p) => (p.province ? p : { ...p, province: zone }));
  }, [open, zone]);

  if (!open) return null;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setS = (k, v) => setShip((p) => ({ ...p, [k]: v }));
  const setI = (k, v) => setInv((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr("");
    const required = ["first_name", "last_name", "email", "phone", "address", "cap", "city", "province"];
    for (const k of required) {
      if (!String(f[k] || "").trim()) { setErr("Compila tutti i campi obbligatori di contatto e indirizzo cliente."); return; }
    }
    if (!/^\S+@\S+\.\S+$/.test(f.email)) { setErr("Inserisci un'email valida."); return; }
    if (!pickup && !shipSame) {
      for (const k of ["address", "cap", "city", "province"]) {
        if (!String(ship[k] || "").trim()) { setErr("Compila tutti i campi obbligatori dell'indirizzo di spedizione."); return; }
      }
    }
    if (tipo === "azienda") {
      if (!inv.ragione_sociale.trim() || !inv.piva.trim() || !inv.codice_fiscale.trim() || !inv.codice_destinatario.trim()) {
        setErr("Per la fattura Azienda servono Ragione sociale, P.IVA, Codice Fiscale e Codice destinatario/PEC (SDI)."); return;
      }
    } else if (!inv.codice_fiscale.trim()) {
      setErr("Per la fattura Privato serve il Codice Fiscale."); return;
    }
    setBusy(true);
    try {
      const useSame = pickup || shipSame;
      const customer = {
        ...f,
        notes: useSame ? "" : ship.notes,
        ship_same: useSame,
        ship_address: useSame ? f.address : ship.address,
        ship_cap: useSame ? f.cap : ship.cap,
        ship_city: useSame ? f.city : ship.city,
        ship_province: useSame ? f.province : ship.province,
        ship_notes: useSame ? "" : ship.notes,
        invoice: {
          wants_invoice: true,
          tipo,
          ragione_sociale: tipo === "azienda" ? inv.ragione_sociale : null,
          piva: tipo === "azienda" ? inv.piva : null,
          codice_fiscale: inv.codice_fiscale,
          codice_destinatario: tipo === "azienda" ? inv.codice_destinatario : null,
        },
      };
      const isCart = Array.isArray(cartItems) && cartItems.length > 0;
      const endpoint = isCart ? "/api/shop/cart/checkout" : "/api/shop/checkout";
      const payload = isCart
        ? { items: cartItems.map((c) => ({ slug: c.slug, qty: c.qty })), zone: pickup ? null : zone, pickup, origin_url: window.location.origin, customer }
        : { slug, qty, zone: pickup ? null : zone, pickup, origin_url: window.location.origin, customer };
      const r = await fetch(`${API}${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.checkout_url) { if (onSuccessClearCart) onSuccessClearCart(); window.location.href = d.checkout_url; }
      else { setErr(d.detail || "Errore nella creazione del pagamento."); setBusy(false); }
    } catch { setErr("Errore di connessione."); setBusy(false); }
  };

  return (
    <div data-testid="checkout-funnel" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-2xl my-8 bg-[#0b0b0b] border border-white/15 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em]" style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}>
            <ShoppingCart size={15} /> Dati per l'ordine
          </div>
          <button data-testid="funnel-close" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {quote && (
            <div className="border border-white/10 bg-white/[0.03] p-4 text-sm">
              {Array.isArray(cartItems) && cartItems.length > 0 ? (
                <div className="space-y-1 mb-2">
                  {(quote.items || []).map((it) => (
                    <div key={it.slug} className="flex justify-between text-white/70"><span>{it.model || it.name} × {it.qty}</span><span>{eur(it.prezzo_netto_totale)}</span></div>
                  ))}
                </div>
              ) : (
                <div className="flex justify-between text-white/70"><span>{productName} × {qty}</span><span>{eur(quote.prezzo_netto_totale)}</span></div>
              )}
              <div className="flex justify-between text-white/50 text-xs mt-1"><span>{pickup ? "Ritiro in fabbrica" : `Trasporto — ${zone}`}</span><span>{pickup ? "Gratis" : eur(quote.trasporto)}</span></div>
              <div className="flex justify-between text-white/50 text-xs"><span>IVA {(quote.iva_rate * 100).toFixed(0)}%</span><span>{eur(quote.iva)}</span></div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10"><span className="text-white/70">Totale</span><span className="text-xl font-black" style={{ color: brand.yellow, fontFamily: "Archivo" }}>{eur(quote.totale)}</span></div>
            </div>
          )}

          <div>
            <p className="text-white/60 text-sm font-semibold mb-3">Contatto</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label><span className={lbl}>Nome *</span><input data-testid="funnel-first-name" className={inp} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} /></label>
              <label><span className={lbl}>Cognome *</span><input data-testid="funnel-last-name" className={inp} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} /></label>
              <label><span className={lbl}>Email *</span><input data-testid="funnel-email" type="email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} /></label>
              <label><span className={lbl}>Telefono *</span><input data-testid="funnel-phone" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></label>
            </div>
          </div>

          <div>
            <p className="text-white/60 text-sm font-semibold mb-3">Indirizzo cliente</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="sm:col-span-2"><span className={lbl}>Via e civico *</span><input data-testid="funnel-address" className={inp} value={f.address} onChange={(e) => set("address", e.target.value)} /></label>
              <label><span className={lbl}>CAP *</span><input data-testid="funnel-cap" className={inp} value={f.cap} onChange={(e) => set("cap", e.target.value)} /></label>
              <label><span className={lbl}>Città *</span><input data-testid="funnel-city" className={inp} value={f.city} onChange={(e) => set("city", e.target.value)} /></label>
              <label><span className={lbl}>Provincia (sigla) *</span><input data-testid="funnel-province" className={inp} value={f.province} onChange={(e) => set("province", e.target.value.toUpperCase())} maxLength={2} /></label>
            </div>
          </div>

          {pickup ? (
            <div className="border border-white/15 bg-white/[0.03] p-4">
              <p className="text-white/80 text-sm font-semibold">Ritiro in fabbrica</p>
              <p className="text-white/50 text-xs mt-1">Ti contatteremo per concordare data e orario di ritiro presso la sede. Nessun costo di trasporto.</p>
            </div>
          ) : (
          <div className="border-t border-white/10 pt-4">
            <p className="text-white/60 text-sm font-semibold mb-3">Indirizzo di spedizione</p>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input data-testid="funnel-ship-same" type="checkbox" checked={shipSame} onChange={(e) => setShipSame(e.target.checked)} className="accent-yellow-400 w-4 h-4" />
              <span className="text-white/80 text-sm">Coincide con l'indirizzo del cliente</span>
            </label>
            {!shipSame && (
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="sm:col-span-2"><span className={lbl}>Via e civico *</span><input data-testid="funnel-ship-address" className={inp} value={ship.address} onChange={(e) => setS("address", e.target.value)} /></label>
                <label><span className={lbl}>CAP *</span><input data-testid="funnel-ship-cap" className={inp} value={ship.cap} onChange={(e) => setS("cap", e.target.value)} /></label>
                <label><span className={lbl}>Città *</span><input data-testid="funnel-ship-city" className={inp} value={ship.city} onChange={(e) => setS("city", e.target.value)} /></label>
                <label><span className={lbl}>Provincia (sigla) *</span><input data-testid="funnel-ship-province" className={inp} value={ship.province} onChange={(e) => setS("province", e.target.value.toUpperCase())} maxLength={2} /></label>
                <label className="sm:col-span-2"><span className={lbl}>Note per il corriere</span><input data-testid="funnel-ship-notes" className={inp} value={ship.notes} onChange={(e) => setS("notes", e.target.value)} placeholder="es. citofono, orari" /></label>
              </div>
            )}
            {shipSame && (
              <label className="block"><span className={lbl}>Note per il corriere</span><input data-testid="funnel-notes" className={inp} value={ship.notes} onChange={(e) => setS("notes", e.target.value)} placeholder="es. citofono, orari" /></label>
            )}
            {(() => {
              const sp = shipSame ? f.province : ship.province;
              return sp && zone && sp !== zone ? (
                <p className="text-amber-400/80 text-xs mt-2">Nota: la provincia ({sp}) è diversa dalla zona di consegna del preventivo ({zone}). Il trasporto è calcolato su {zone}.</p>
              ) : null;
            })()}
          </div>
          )}

          <div className="border-t border-white/10 pt-4">
            <p className="text-white/80 text-sm font-semibold mb-3">Dati di fatturazione</p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <button data-testid="funnel-invoice-tipo-privato" onClick={() => setTipo("privato")} className={`px-4 py-2 text-sm border transition-colors ${tipo === "privato" ? "border-white bg-white/10 text-white" : "border-white/20 text-white/60"}`}>Privato</button>
                <button data-testid="funnel-invoice-tipo-azienda" onClick={() => setTipo("azienda")} className={`px-4 py-2 text-sm border transition-colors ${tipo === "azienda" ? "border-white bg-white/10 text-white" : "border-white/20 text-white/60"}`}>Azienda</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {tipo === "azienda" && (
                  <>
                    <label className="sm:col-span-2"><span className={lbl}>Ragione sociale *</span><input data-testid="funnel-ragione-sociale" className={inp} value={inv.ragione_sociale} onChange={(e) => setI("ragione_sociale", e.target.value)} /></label>
                    <label><span className={lbl}>P.IVA *</span><input data-testid="funnel-piva" className={inp} value={inv.piva} onChange={(e) => setI("piva", e.target.value)} /></label>
                    <label><span className={lbl}>Codice destinatario / PEC (SDI) *</span><input data-testid="funnel-sdi" className={inp} value={inv.codice_destinatario} onChange={(e) => setI("codice_destinatario", e.target.value.toUpperCase())} placeholder="es. Codice SDI o PEC" /></label>
                  </>
                )}
                <label><span className={lbl}>Codice Fiscale *</span><input data-testid="funnel-cf" className={inp} value={inv.codice_fiscale} onChange={(e) => setI("codice_fiscale", e.target.value.toUpperCase())} /></label>
              </div>
            </div>
          </div>

          {err && <p data-testid="funnel-error" className="text-sm text-red-400">{err}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm"><ArrowLeft size={15} /> Indietro</button>
          <button data-testid="funnel-submit" onClick={submit} disabled={busy}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.02] disabled:opacity-50"
            style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
            {busy ? <><Loader2 size={16} className="animate-spin" /> Reindirizzo…</> : <><ShoppingCart size={16} /> Vai al pagamento</>}
          </button>
        </div>
      </div>
    </div>
  );
};
